/**
 * Single source of truth for the doimpo company deck (/deck).
 * Rendered by DeckSlides, and flattened to crawler HTML by deckSeoHtml().
 * Keep copy short and human — one idea per slide.
 */
import { SERVICES } from "@/lib/services";
import { TESTIMONIALS, type Testimonial } from "@/lib/testimonials";

export const DECK_URL = "https://doimpo.com/deck";
export const DECK_TITLE = "doimpo Company Deck — One Partner for Strategy, Brand, Web & Growth";
export const DECK_DESCRIPTION =
  "Meet doimpo in a few slides: who we are, what we stand for, the eight ways we help, real client work, and how we start. One partner for strategy, brand, product and marketing.";
export const DECK_OG_IMAGE = "https://doimpo.com/media/brand/og-doimpo-deck.jpg";
export const DECK_FOOTER = "doimpo.com · hello@doimpo.com · Hyderabad · Serving US · UK · AU · UAE";

export type DeckRow = { from: string; to: string };
export type DeckFlow = { trigger: string; action: string; outcome: string };
export type DeckCard = { title: string; body?: string; href?: string };
/** Before → the move we made → the growth engine it unlocked. */
export type DeckGrowthArc = [before: string, move: string, engine: string];

export const GROWTH_ARC_LABELS = ["Before", "The move", "Growth engine"] as const;

export type DeckSlide =
  | {
      kind: "cover";
      id: string;
      label: string;
      title: string;
      accent: string;
      body: string;
      chips: string[];
      image: string;
      imageAlt: string;
    }
  | {
      kind: "story";
      id: string;
      label: string;
      title: string;
      accent?: string;
      body: string;
      lines?: string[];
      chips?: string[];
      image: string;
      imageAlt: string;
      imageSide: "left" | "right";
    }
  | { kind: "compare"; id: string; label: string; title: string; left: string; right: string; rows: DeckRow[] }
  | { kind: "converge"; id: string; label: string; title: string; body: string; pieces: string[] }
  | { kind: "values"; id: string; label: string; title: string; accent: string; cards: DeckCard[] }
  | { kind: "stack"; id: string; label: string; title: string; body: string; layers: DeckCard[] }
  | { kind: "grid"; id: string; label: string; title: string; body?: string; cards: DeckCard[]; link?: { href: string; label: string } }
  | { kind: "flows"; id: string; label: string; title: string; flows: DeckFlow[] }
  | {
      kind: "case";
      id: string;
      label: string;
      client: string;
      title: string;
      body: string;
      delivered: string[];
      outcome: string;
      image: string;
      imageAlt: string;
      href: string;
    }
  | { kind: "hook"; id: string; label: string; title: string; accent: string }
  | {
      kind: "growth";
      id: string;
      label: string;
      client: string;
      hook: string;
      arc: DeckGrowthArc;
      image: string;
      mobileImage?: string;
      imageAlt: string;
      href: string;
    }
  | { kind: "quote"; id: string; label: string; title: string; quotes: Testimonial[] }
  | { kind: "process"; id: string; label: string; title: string; steps: DeckCard[] }
  | {
      kind: "cta";
      id: string;
      label: string;
      title: string;
      accent: string;
      body: string;
      image: string;
      imageAlt: string;
    };

const SERVICE_ONE_LINERS: Record<(typeof SERVICES)[number]["slug"], string> = {
  "strategy-consulting": "Know where you're going — and why.",
  "branding-design": "Look, sound and feel like you.",
  "digital-marketing": "Get found by the right people.",
  "web-development": "Fast sites that turn visits into calls.",
  "mobile-app-development": "Apps people actually keep.",
  "ai-solutions": "Hand the busywork to machines.",
  "content-creation": "Photo, film and words that connect.",
  "ecommerce-solutions": "Stores that sell while you sleep.",
};

export const DECK_SLIDES: DeckSlide[] = [
  {
    kind: "cover",
    id: "cover",
    label: "Hello, we're doimpo",
    title: "You focus on growth.",
    accent: "We handle the rest.",
    body: "One partner for strategy, brand, product and marketing — so your vision arrives whole.",
    chips: ["Strategy", "Brand", "Web & apps", "AI", "Marketing", "Content"],
    image: "/media/images/deck/deck-cover.webp",
    imageAlt: "Two doimpo team members smiling over brand sketches and a laptop at dusk",
  },
  {
    kind: "story",
    id: "who-we-are",
    label: "Who we are",
    title: "A small team that",
    accent: "thinks like founders.",
    body: "Strategists, designers, engineers, marketers and filmmakers under one roof — working with teams in the US, UK, Australia, the UAE and India.",
    chips: ["Strategy", "Design", "Engineering", "Marketing", "Film & photo", "AI"],
    image: "/media/images/deck/deck-team.webp",
    imageAlt: "The doimpo team collaborating on a moodboard, a website design and a video edit",
    imageSide: "right",
  },
  {
    kind: "story",
    id: "the-problem",
    label: "Sound familiar?",
    title: "Five vendors.",
    accent: "Five versions of your vision.",
    body: "Nobody's work is bad. It just never belongs to the same idea.",
    lines: [
      "A designer in one Slack.",
      "A developer in another.",
      "A marketer who never saw the brief.",
      "And you, managing all of it.",
    ],
    image: "/media/images/deck/deck-problem.webp",
    imageAlt: "A tired founder late at night surrounded by laptops, phones and scattered briefs",
    imageSide: "left",
  },
  {
    kind: "compare",
    id: "the-shift",
    label: "The shift",
    title: "There's a calmer way to grow.",
    left: "Today",
    right: "With doimpo",
    rows: [
      { from: "Five vendors", to: "One partner" },
      { from: "Briefs lost in handoffs", to: "One shared playbook" },
      { from: "You chase updates", to: "We bring them to you" },
      { from: "Brand drifts by channel", to: "One voice everywhere" },
      { from: "Launch and forget", to: "Measure, learn, improve" },
    ],
  },
  {
    kind: "converge",
    id: "one-roof",
    label: "One roof",
    title: "Everything your growth needs, in one conversation.",
    body: "Strategy, brand, product and marketing sit at the same table — so decisions take days, not months.",
    pieces: ["Strategy", "Brand", "Web & apps", "Marketing", "Content", "AI"],
  },
  {
    kind: "values",
    id: "what-we-stand-for",
    label: "What we stand for",
    title: "Do important.",
    accent: "Do impossible.",
    cards: [
      { title: "Your vision first", body: "We build your idea, not our template." },
      { title: "Own the outcome", body: "We measure success in your growth, not our deliverables." },
      { title: "Say it plainly", body: "No jargon, no surprises, no hidden work." },
      { title: "Keep moving", body: "Small, steady releases beat one big reveal." },
    ],
  },
  {
    kind: "stack",
    id: "how-it-fits",
    label: "How it fits",
    title: "One team across every layer.",
    body: "Each layer is built knowing what sits above and below it.",
    layers: [
      { title: "Measurement", body: "What's working, in plain numbers" },
      { title: "Marketing & content", body: "Campaigns, SEO, social, film" },
      { title: "AI & automation", body: "Agents and workflows that save hours" },
      { title: "Websites & apps", body: "Fast, findable, built to convert" },
      { title: "Brand & design", body: "How you look, sound and feel" },
      { title: "Strategy", body: "Where you're going and why" },
    ],
  },
  {
    kind: "grid",
    id: "services",
    label: "Services",
    title: "Eight ways we help.",
    cards: SERVICES.map((s) => ({ title: s.label, body: SERVICE_ONE_LINERS[s.slug], href: s.href })),
    link: { href: "/services", label: "Explore all services" },
  },
  {
    kind: "flows",
    id: "ai",
    label: "AI, quietly",
    title: "AI that does the busywork.",
    flows: [
      { trigger: "New enquiry at 11pm", action: "Answered, qualified, booked", outcome: "No lead waits till morning" },
      { trigger: "Customer asks on WhatsApp", action: "Instant answer from your content", outcome: "Your team gets time back" },
      { trigger: "One new blog post", action: "Cut into posts for every channel", outcome: "One idea, everywhere" },
      { trigger: "Weekly report due", action: "Drafted from live data", outcome: "Decisions, not spreadsheets" },
    ],
  },
  {
    kind: "grid",
    id: "who-we-help",
    label: "Who we help",
    title: "Built for people-first businesses.",
    cards: [
      { title: "Healthcare & clinics", body: "Trust before the first appointment", href: "/deck/healthcare" },
      { title: "Events & hospitality", body: "From discovery to a confirmed seat", href: "/deck/hospitality" },
      { title: "SaaS & AI products", body: "A story people get in one screen", href: "/deck/saas" },
      { title: "Retail & e-commerce", body: "Stores that feel curated, not crowded", href: "/deck/ecommerce" },
      { title: "Community & faith", body: "A home people can find and join" },
      { title: "Personal brands", body: "Your reputation, owned — not rented", href: "/deck/personal-brands" },
    ],
    link: { href: "/portfolio", label: "See all work" },
  },
  {
    kind: "case",
    id: "case-livinhotels",
    label: "Work · Hospitality",
    client: "The Livin Hotels",
    title: "A hotel site that feels like the stay, not a template.",
    body: "We branded the stay, then made the hotel's own site the easiest place to book — so guests come direct instead of through an OTA listing.",
    delivered: ["Hospitality brand", "Lockups & marks", "Direct-booking site", "Campaigns"],
    outcome: "A calm, premium hotel brand with a live site that carries direct bookings.",
    image: "/media/portfolio/screenshots/livinhotels-home.webp",
    imageAlt: "The Livin Hotels website homepage",
    href: "/portfolio/livinhotels",
  },
  {
    kind: "case",
    id: "case-zphoenix",
    label: "Work · Events",
    client: "ZPhoenix Events",
    title: "Premium nights, booked without a single WhatsApp thread.",
    body: "Brand and ticketing platform in one — guests discover an event, pick their exact table and check out securely.",
    delivered: ["Brand system", "Events platform", "Seat maps", "Secure checkout"],
    outcome: "A live platform that sells reserved seating end to end.",
    image: "/media/portfolio/screenshots/tiktzphoenix-home.webp",
    imageAlt: "ZPhoenix Events ticketing website homepage",
    href: "/portfolio/tiktzphoenix",
  },
  {
    kind: "case",
    id: "case-ameymarathe",
    label: "Work · Personal brand",
    client: "Chef Amey Marathe",
    title: "A chef's reputation, moved from the feed to a home he owns.",
    body: "We designed Amey's signature lockups and built ameymarathe.com — one place for menus, stories and bookings that outlasts any single channel.",
    delivered: ["Personal brand", "Lockups", "Chef web platform", "Content & campaigns"],
    outcome: "An owned platform that turns an audience into guests, collaborations and press.",
    image: "/media/portfolio/screenshots/ameymarathe-home.webp",
    imageAlt: "Chef Amey Marathe website homepage",
    href: "/portfolio/ameymarathe",
  },
  {
    kind: "quote",
    id: "in-their-words",
    label: "In their words",
    title: "Founders who chose one partner.",
    quotes: TESTIMONIALS,
  },
  {
    kind: "process",
    id: "how-we-start",
    label: "How we start",
    title: "Four steps. No guesswork.",
    steps: [
      { title: "Listen", body: "A conversation about where you're headed." },
      { title: "Blueprint", body: "A clear 90-day plan, written for you." },
      { title: "Build", body: "Brand, product and campaigns, shipped in parallel." },
      { title: "Grow", body: "We measure, learn and keep improving." },
    ],
  },
  {
    kind: "cta",
    id: "lets-talk",
    label: "Let's talk",
    title: "Let's build",
    accent: "what's next.",
    body: "Tell us where you want to be in a year. We'll show you the shortest path there.",
    image: "/media/images/deck/deck-cta.webp",
    imageAlt: "A relaxed founder smiling on a video call at a tidy desk",
  },
];

function esc(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function list(items: string[]): string {
  return `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>`;
}

export type DeckDefinition = {
  /** Empty for the company deck, otherwise the /deck/<slug> segment. */
  slug: string;
  path: string;
  url: string;
  title: string;
  description: string;
  ogImage: string;
  ogImageAlt: string;
  /** Header label, e.g. "Healthcare deck". */
  label: string;
  /** utm_campaign for every booking link in the deck. */
  campaign: string;
  shareText: string;
  slides: DeckSlide[];
};

export function deckBookHref(campaign: string): string {
  return `/book?utm_source=deck&utm_medium=deck&utm_campaign=${encodeURIComponent(campaign)}`;
}

export const COMPANY_DECK: DeckDefinition = {
  slug: "",
  path: "/deck",
  url: DECK_URL,
  title: DECK_TITLE,
  description: DECK_DESCRIPTION,
  ogImage: DECK_OG_IMAGE,
  ogImageAlt: "doimpo company deck — You focus on growth. We handle the rest.",
  label: "Company deck",
  campaign: "company-deck",
  shareText: "Meet doimpo — one partner for strategy, brand, product and growth.",
  slides: DECK_SLIDES,
};

/** Full deck text as semantic HTML for crawlers and the no-JS prerender. */
export function deckSeoHtml(deck: DeckDefinition = COMPANY_DECK): string {
  const sections = deck.slides.map((s, i) => {
    const n = String(i + 1).padStart(2, "0");
    const head = (title: string) =>
      i === 0 ? `<h1>${esc(title)}</h1>` : `<h2>${esc(title)}</h2>`;
    let inner = "";
    switch (s.kind) {
      case "cover":
        inner = `${head(`${s.title} ${s.accent}`)}<p>${esc(s.body)}</p>${list(s.chips)}`;
        break;
      case "cta":
        inner = `${head(`${s.title} ${s.accent}`)}<p>${esc(s.body)}</p>`;
        break;
      case "story":
        inner = `${head(`${s.title} ${s.accent ?? ""}`.trim())}<p>${esc(s.body)}</p>${s.lines ? list(s.lines) : ""}${s.chips ? list(s.chips) : ""}`;
        break;
      case "compare":
        inner = `${head(s.title)}<ul>${s.rows.map((r) => `<li>${esc(r.from)} → ${esc(r.to)}</li>`).join("")}</ul>`;
        break;
      case "converge":
        inner = `${head(s.title)}<p>${esc(s.body)}</p>${list(s.pieces)}`;
        break;
      case "values":
        inner = `${head(`${s.title} ${s.accent}`)}<ul>${s.cards.map((c) => `<li><strong>${esc(c.title)}</strong> — ${esc(c.body ?? "")}</li>`).join("")}</ul>`;
        break;
      case "stack":
        inner = `${head(s.title)}<p>${esc(s.body)}</p><ul>${s.layers.map((c) => `<li><strong>${esc(c.title)}</strong> — ${esc(c.body ?? "")}</li>`).join("")}</ul>`;
        break;
      case "grid":
        inner = `${head(s.title)}<ul>${s.cards
          .map((c) =>
            c.href
              ? `<li><a href="${esc(c.href)}">${esc(c.title)}</a> — ${esc(c.body ?? "")}</li>`
              : `<li><strong>${esc(c.title)}</strong> — ${esc(c.body ?? "")}</li>`,
          )
          .join("")}</ul>`;
        break;
      case "flows":
        inner = `${head(s.title)}<ul>${s.flows.map((f) => `<li>${esc(f.trigger)} → ${esc(f.action)} → ${esc(f.outcome)}</li>`).join("")}</ul>`;
        break;
      case "case":
        inner = `${head(`${s.client}: ${s.title}`)}<p>${esc(s.body)}</p>${list(s.delivered)}<p>${esc(s.outcome)} <a href="${esc(s.href)}">Read the case study</a></p>`;
        break;
      case "hook":
        inner = head(`${s.title} ${s.accent}`);
        break;
      case "growth":
        inner = `${head(`${s.client}: ${s.hook}`)}<ol>${s.arc
          .map((step, j) => `<li><strong>${GROWTH_ARC_LABELS[j]}</strong> — ${esc(step)}</li>`)
          .join("")}</ol><p><a href="${esc(s.href)}">Read the ${esc(s.client)} growth story</a></p>`;
        break;
      case "quote":
        inner = `${head(s.title)}${s.quotes
          .map(
            (q) =>
              `<blockquote><p>${esc(q.quote)}</p><cite>${esc(q.name)}, ${esc(q.role)}, ${esc(q.company)}, ${esc(q.country)}</cite></blockquote>`,
          )
          .join("")}`;
        break;
      case "process":
        inner = `${head(s.title)}<ol>${s.steps.map((c) => `<li><strong>${esc(c.title)}</strong> — ${esc(c.body ?? "")}</li>`).join("")}</ol>`;
        break;
    }
    return `<section id="${esc(s.id)}" aria-label="Slide ${n}: ${esc(s.label)}">${inner}</section>`;
  }).join("");
  return `<article>${sections}<p><a href="${esc(deckBookHref(deck.campaign))}">Book a call</a> · <a href="mailto:hello@doimpo.com">hello@doimpo.com</a></p></article>`;
}

export function deckJsonLd(deck: DeckDefinition = COMPANY_DECK): Record<string, unknown>[] {
  const doc = {
    "@context": "https://schema.org",
    "@type": "PresentationDigitalDocument",
    name: deck.title,
    description: deck.description,
    url: deck.url,
    image: deck.ogImage,
    inLanguage: "en",
    author: { "@type": "Organization", name: "doimpo", url: "https://doimpo.com" },
    publisher: { "@type": "Organization", name: "doimpo", url: "https://doimpo.com" },
  };
  const stories = deck.slides.filter((s): s is Extract<DeckSlide, { kind: "growth" }> => s.kind === "growth");
  if (stories.length) {
    return [
      doc,
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${deck.label}: growth stories`,
        itemListElement: stories.map((s, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "CreativeWork",
            name: s.client,
            description: s.hook,
            url: `https://doimpo.com${s.href}`,
            creator: { "@type": "Organization", name: "doimpo" },
          },
        })),
      },
    ];
  }
  return [
    doc,
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "doimpo services",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.label,
          description: s.description,
          url: `https://doimpo.com${s.href}`,
          provider: { "@type": "Organization", name: "doimpo" },
        },
      })),
    },
  ];
}
